import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { DEFAULT_DATE_RANGE_PRESET } from '../../components/notifications/constants';
import {
  NotificationCenterViewState,
  NotificationCursor,
  NotificationRecord,
  NotificationsState,
  FilterCacheEntry,
  DEFAULT_FILTER_KEY,
} from '../types/notifications';

const createEmptyFilterCache = (): FilterCacheEntry => ({
  items: {},
  cursor: null,
  hasMore: true,
  isLoading: false,
});

// Stable empty cache reference to avoid creating new objects on each selector call
const EMPTY_FILTER_CACHE: FilterCacheEntry = Object.freeze({
  items: {},
  cursor: null,
  hasMore: true,
  isLoading: false,
});

const EMPTY_ITEMS: Record<string, NotificationRecord> = Object.freeze({});

const initialState: NotificationsState = {
  filterCache: {
    [DEFAULT_FILTER_KEY]: createEmptyFilterCache(),
  },
  activeFilterKey: DEFAULT_FILTER_KEY,
  unseenCount: 0,
  readAllBefore: null,
  isCenterOpen: false,
  viewState: {
    datePreset: DEFAULT_DATE_RANGE_PRESET,
    lastOpenedNotificationKey: null,
    search: '',
    scrollTop: 0,
  },
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    setActiveFilter(state, action: PayloadAction<string>) {
      const filterKey = action.payload;

      // Initialize cache for this filter if it doesn't exist
      const existingCache = state.filterCache[filterKey];
      const newFilterCache = existingCache
        ? { ...state.filterCache }
        : {
            ...state.filterCache,
            [filterKey]: createEmptyFilterCache(),
          };

      return {
        ...state,
        activeFilterKey: filterKey,
        filterCache: newFilterCache,
      };
    },

    setFilterNotifications(
      state,
      action: PayloadAction<{
        filterKey: string;
        notifications: Record<string, NotificationRecord>;
        cursor: NotificationCursor | null;
        hasMore: boolean;
      }>
    ) {
      const { filterKey, notifications, cursor, hasMore } = action.payload;

      return {
        ...state,
        filterCache: {
          ...state.filterCache,
          [filterKey]: {
            items: notifications,
            cursor,
            hasMore,
            isLoading: false,
          },
        },
      };
    },

    appendFilterNotifications(
      state,
      action: PayloadAction<{
        filterKey: string;
        notifications: Record<string, NotificationRecord>;
        cursor: NotificationCursor | null;
        hasMore: boolean;
      }>
    ) {
      const { filterKey, notifications, cursor, hasMore } = action.payload;

      const existingCache = state.filterCache[filterKey] ?? createEmptyFilterCache();
      const existingItems = existingCache.items;
      const newItems = { ...existingItems };

      // Add new notifications, avoiding duplicates
      Object.entries(notifications).forEach(([key, notification]) => {
        if (!newItems[key]) {
          newItems[key] = notification;
        }
      });

      return {
        ...state,
        filterCache: {
          ...state.filterCache,
          [filterKey]: {
            items: newItems,
            cursor,
            hasMore,
            isLoading: false,
          },
        },
      };
    },

    addNotificationToFilter(
      state,
      action: PayloadAction<{
        filterKey: string;
        key: string;
        notification: NotificationRecord;
      }>
    ) {
      const { filterKey, key, notification } = action.payload;

      const existingCache = state.filterCache[filterKey] ?? createEmptyFilterCache();

      // Skip if already exists (deduplication)
      if (existingCache.items[key]) {
        return state;
      }

      return {
        ...state,
        filterCache: {
          ...state.filterCache,
          [filterKey]: {
            ...existingCache,
            items: {
              ...existingCache.items,
              [key]: notification,
            },
          },
        },
      };
    },

    updateNotificationInFilter(
      state,
      action: PayloadAction<{
        filterKey: string;
        key: string;
        notification: NotificationRecord;
      }>
    ) {
      const { filterKey, key, notification } = action.payload;

      const existingCache = state.filterCache[filterKey];
      if (!existingCache || !existingCache.items[key]) {
        return state;
      }

      return {
        ...state,
        filterCache: {
          ...state.filterCache,
          [filterKey]: {
            ...existingCache,
            items: {
              ...existingCache.items,
              [key]: notification,
            },
          },
        },
      };
    },

    removeNotificationFromFilter(
      state,
      action: PayloadAction<{
        filterKey: string;
        key: string;
      }>
    ) {
      const { filterKey, key } = action.payload;

      const existingCache = state.filterCache[filterKey];
      if (!existingCache) {
        return state;
      }

      const { [key]: _removed, ...remainingItems } = existingCache.items;

      return {
        ...state,
        filterCache: {
          ...state.filterCache,
          [filterKey]: {
            ...existingCache,
            items: remainingItems,
          },
        },
      };
    },

    setFilterLoading(
      state,
      action: PayloadAction<{
        filterKey: string;
        isLoading: boolean;
      }>
    ) {
      const { filterKey, isLoading } = action.payload;

      const existingCache = state.filterCache[filterKey] ?? createEmptyFilterCache();

      return {
        ...state,
        filterCache: {
          ...state.filterCache,
          [filterKey]: {
            ...existingCache,
            isLoading,
          },
        },
      };
    },

    setInitialState(
      state,
      action: PayloadAction<{
        filterKey: string;
        notifications: Record<string, NotificationRecord>;
        cursor: NotificationCursor | null;
        hasMore: boolean;
        unseenCount: number;
        readAllBefore?: number | null;
      }>
    ) {
      const { filterKey, notifications, cursor, hasMore, unseenCount, readAllBefore } = action.payload;

      return {
        ...state,
        activeFilterKey: filterKey,
        filterCache: {
          ...state.filterCache,
          [filterKey]: {
            items: notifications,
            cursor,
            hasMore,
            isLoading: false,
          },
        },
        unseenCount,
        readAllBefore: readAllBefore == null ? state.readAllBefore : Math.max(state.readAllBefore ?? 0, readAllBefore),
      };
    },

    clearNotifications(state) {
      return {
        ...initialState,
        isCenterOpen: state.isCenterOpen,
      };
    },

    openNotificationCenter(state) {
      return {
        ...state,
        isCenterOpen: true,
      };
    },

    closeNotificationCenter(state) {
      return {
        ...state,
        isCenterOpen: false,
      };
    },

    setUnseenCount(state, action: PayloadAction<number>) {
      return {
        ...state,
        unseenCount: action.payload,
      };
    },

    setReadAllBefore(state, action: PayloadAction<number>) {
      state.readAllBefore = Math.max(state.readAllBefore ?? 0, action.payload);
    },

    restoreUnseenCountIfZero(state, action: PayloadAction<number>) {
      if (state.unseenCount === 0) {
        state.unseenCount = action.payload;
      }
    },

    setNotificationCenterViewState(state, action: PayloadAction<Partial<NotificationCenterViewState>>) {
      state.viewState = {
        ...state.viewState,
        ...action.payload,
      };
    },

    setLoadedNotificationsReadState(
      state,
      action: PayloadAction<{
        keys: string[];
        readAt: number | null;
        expectedReadAt?: number;
      }>
    ) {
      Object.values(state.filterCache).forEach((cache) => {
        action.payload.keys.forEach((key) => {
          const notification = cache.items[key];
          if (!notification) return;

          if (action.payload.expectedReadAt !== undefined && notification.readAt !== action.payload.expectedReadAt)
            return;

          cache.items[key] = {
            ...notification,
            readAt: action.payload.readAt,
          };
        });
      });
    },
  },
});

// Selectors - using explicit types to avoid circular dependency issues
type NotificationsSliceState = { notifications: NotificationsState };

export const selectActiveFilterKey = (state: NotificationsSliceState): string => state.notifications.activeFilterKey;

const selectActiveFilterCache = (state: NotificationsSliceState): FilterCacheEntry => {
  const filterKey = state.notifications.activeFilterKey;
  return state.notifications.filterCache[filterKey] ?? EMPTY_FILTER_CACHE;
};

export const selectActiveFilterItems = (state: NotificationsSliceState): Record<string, NotificationRecord> => {
  const filterKey = state.notifications.activeFilterKey;
  const cache = state.notifications.filterCache[filterKey];
  return cache?.items ?? EMPTY_ITEMS;
};

export const selectActiveFilterHasMore = (state: NotificationsSliceState): boolean => {
  const cache = selectActiveFilterCache(state);
  return cache.hasMore;
};

export const selectActiveFilterIsLoading = (state: NotificationsSliceState): boolean => {
  const cache = selectActiveFilterCache(state);
  return cache.isLoading;
};

export const selectActiveFilterCursor = (state: NotificationsSliceState): NotificationCursor | null => {
  const cache = selectActiveFilterCache(state);
  return cache.cursor;
};

export const selectUnseenCount = (state: NotificationsSliceState): number => state.notifications.unseenCount;
export const selectReadAllBefore = (state: NotificationsSliceState): number | null => state.notifications.readAllBefore;
export const selectIsCenterOpen = (state: NotificationsSliceState): boolean => state.notifications.isCenterOpen;
export const selectNotificationCenterViewState = (state: NotificationsSliceState): NotificationCenterViewState =>
  state.notifications.viewState;

export const {
  setActiveFilter,
  setFilterNotifications,
  appendFilterNotifications,
  addNotificationToFilter,
  updateNotificationInFilter,
  removeNotificationFromFilter,
  setFilterLoading,
  setInitialState,
  clearNotifications,
  openNotificationCenter,
  closeNotificationCenter,
  setUnseenCount,
  setReadAllBefore,
  restoreUnseenCountIfZero,
  setNotificationCenterViewState,
  setLoadedNotificationsReadState,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
